/**
 * Media Library V1 frontend API client.
 *
 * Wallet-scoped, private. Reuses the EXACT hardened management authority already
 * used by Territory Identity / LOD0 Brand Image: SIWE session cookie
 * (`credentials: "include"`) + memory-only session-bound CSRF token (shared via
 * territory-identity-api, so a wallet switch clears it once) + browser-attached
 * trusted Origin. Uploads/deletes are state-changing and always carry CSRF with
 * a single transparent refresh-on-403 retry.
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";

export { clearCsrfToken };

export type MediaType = "image" | "gif" | "pdf" | "video";

export interface MediaAsset {
  asset_id: string;
  media_type: MediaType;
  mime_type: string;
  filename: string | null;
  byte_size: number;
  sha256: string;
  status: "processing" | "active" | "failed";
  metadata: Record<string, string | number | boolean>;
  content_url: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface MediaUsage {
  used_bytes: number;
  limit_bytes: number;
  available_bytes: number;
}

export class MediaApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

interface ErrorEnvelope {
  code?: string;
  message?: string;
  detail?: { code?: string; message?: string };
}

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    const code = env.code ?? env.detail?.code ?? "MEDIA_ERROR";
    const message = env.message ?? env.detail?.message ?? resp.statusText;
    throw new MediaApiError(resp.status, code, message);
  }
  return body;
}

/** Absolute, cookie-authenticated content URL (same-origin via ingress). */
export function mediaContentUrl(asset: MediaAsset): string {
  return `${API_BASE}${asset.content_url}?v=${encodeURIComponent(asset.sha256.slice(0, 12))}`;
}

export async function fetchUsage(signal?: AbortSignal): Promise<MediaUsage> {
  const resp = await fetch(`${API_BASE}/api/v1/media/usage`, { credentials: "include", signal });
  const body = (await parse(resp)) as { usage: MediaUsage };
  return body.usage;
}

export async function listAssets(
  cursor: string | null,
  pageSize: number,
  signal?: AbortSignal,
): Promise<{ assets: MediaAsset[]; next_cursor: string | null }> {
  const params = new URLSearchParams({ page_size: String(pageSize) });
  if (cursor) params.set("cursor", cursor);
  const resp = await fetch(`${API_BASE}/api/v1/media/assets?${params.toString()}`, {
    credentials: "include",
    signal,
  });
  return (await parse(resp)) as { assets: MediaAsset[]; next_cursor: string | null };
}

export async function uploadAsset(file: File, signal?: AbortSignal): Promise<MediaAsset> {
  const doPut = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}/api/v1/media/assets`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-The-Grid-CSRF": csrf,
        "X-Media-Filename": encodeURIComponent(file.name).slice(0, 255),
      },
      body: file,
      signal,
    });
  let resp = await doPut(await getCsrfToken());
  if (resp.status === 403) {
    clearCsrfToken();
    resp = await doPut(await getCsrfToken(true));
  }
  const body = (await parse(resp)) as { asset: MediaAsset };
  return body.asset;
}

export async function deleteAsset(assetId: string, signal?: AbortSignal): Promise<void> {
  const doDelete = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}/api/v1/media/assets/${encodeURIComponent(assetId)}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "X-The-Grid-CSRF": csrf },
      signal,
    });
  let resp = await doDelete(await getCsrfToken());
  if (resp.status === 403) {
    clearCsrfToken();
    resp = await doDelete(await getCsrfToken(true));
  }
  await parse(resp);
}
